import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ImportExcel from '../../Components/ImportExcel';
import ExportExcel from '../../Components/ExportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  handleCopy,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';


const ADD_NEW_EMPLOYEE = '__add_new__';

const normalizeEmployeeName = (value) => (value || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getImportEmployeeName = (row) => row.name || row.employee || row['employee name'] || row.employeename || '';

const getEmployeeFirstLastName = (employee) => [employee?.[3], employee?.[4]]
  .filter(Boolean)
  .join(' ')
  .trim();

const getEmployeeDisplayName = (employee) => getEmployeeFirstLastName(employee) || employee?.[1] || '';

const getEmployeeLookupName = (employee) => getEmployeeFirstLastName(employee) || employee?.[5] || employee?.[1] || '';

function EmployeeUtilisationTable({
  name,
  rows,
  companies,
  organisations,
  dates,
  activeFilter,
  employees,
  selectedCompany,
  selectedOrganisation,
  selectedDates,
  selectedApis,
  orgLength,
  onClickedAdd,
  onClickedDelete,
  getStateData,
  getEmployees,
  resetReportingState,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    DimDateKey: 'All',
    DimEmployeeKey: 'All',
    DimCompanyKey: 'All',
    DimOrganisationKey: 'All',
    ActualFees: '',
    ForecastFees: '',
    ActualFullTimeEmployees: '',
    ForecastRequiredFullTimeEmployees: '',
    WorkDaysInPeriod: '',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);
  const [open, setOpen] = useState(false);
  const [exportAllData, setExportAllData] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [pendingImportRows, setPendingImportRows] = useState([]);
  const [employeeImportRecords, setEmployeeImportRecords] = useState([]);
  const [employeeImportResolutions, setEmployeeImportResolutions] = useState({});

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'DimDateKey':
      case 'AverageHoursWorkedPerDay':
      case 'ActualFteEquivalentMTD':
      case 'HoursWorkedMTD':
      case 'ChargeableHoursWorkedMTD':
      case 'NonChargeableHoursWorkedMTD':
        return (a, b) => {
          return a[sortColumn] - b[sortColumn];
        };

      case 'DimCompanyKey':
        return (a, b) => {
          const aName = getCompanyNameById(a[sortColumn]);
          const bName = getCompanyNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimOrganisationKey':
        return (a, b) => {
          const aName = getOrganisationNameById(a[sortColumn]);
          const bName = getOrganisationNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimEmployeeKey':
        return (a, b) => {
          const aName = getEmployeeNameById(a[sortColumn]);
          const bName = getEmployeeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    // Normalise rows: some endpoints return { data: [...], state: {...} }
    const rawRows = Array.isArray(rows)
      ? rows
      : rows && Array.isArray(rows.data)
        ? rows.data
        : [];

    let startMonth = companies.find((com) => com.id === selectedCompany)
      ? companies.find((com) => com.id === selectedCompany).FiscalYearStartMonth
      : 7;

    let startDate;
    let endDate;

    if (new Date().getMonth() >= startMonth - 1) {
      startDate = new Date().getFullYear() * 10000 + startMonth * 100 + 1;
      endDate =
        (new Date().getFullYear() + 1) * 10000 + (startMonth - 1) * 100 + 1;
    } else {
      startDate = (new Date().getFullYear() - 1) * 10000 + startMonth * 100 + 1;
      endDate =
        new Date().getFullYear() * 10000 + (startMonth - 1) * 100 + 1;
    }

    let filterRows = [];

    if (activeFilter) {
      filterRows = rawRows.filter(
        (row) => row.DimDateKey >= startDate && row.DimDateKey <= endDate
      );
    } else {
      filterRows = rawRows;
    }

    // Sort a copy so we never mutate props
    const sortedRows = [...filterRows].sort((a, b) => {
      // Handle null/undefined DimDateKey values
      if (!a.DimDateKey && !b.DimDateKey) return 0;
      if (!a.DimDateKey) return 1;  // Push null/undefined dates to end
      if (!b.DimDateKey) return -1; // Push null/undefined dates to end

      // First compare by date (descending)
      if (b.DimDateKey !== a.DimDateKey) {
        return b.DimDateKey - a.DimDateKey;
      }

      // If dates are equal, sort by employee
      if (!a.DimEmployeeKey && !b.DimEmployeeKey) return 0;
      if (!a.DimEmployeeKey) return 1;
      if (!b.DimEmployeeKey) return -1;

      const employeeA = employees.find(
        (employee) => employee[0] === a.DimEmployeeKey
      );
      const employeeB = employees.find(
        (employee) => employee[0] === b.DimEmployeeKey
      );

      if (!employeeA && !employeeB) return 0;
      if (!employeeA) return 1;
      if (!employeeB) return -1;

      const nameA = getEmployeeDisplayName(employeeA);
      const nameB = getEmployeeDisplayName(employeeB);
      return nameA.localeCompare(nameB);
    });

    setTableRows(sortedRows);
  }, [rows, employees, activeFilter, companies, selectedCompany]);


  const getCompanyNameById = (id) => {
    if (!id) return '';
    const company = companies?.find(company => company.id === id);
    return company ? company.CompanyName : '';
  };

  const getOrganisationNameById = (id) => {
    if (!id) return '';
    const organisation = organisations?.find(organisation => organisation.id === id);
    return organisation ? organisation.OrgName : '';
  };
  
  const getEmployeeNameById = (id) => {
    if (!id) return '';
    const employee = employees?.find(employee => employee[0] === id);
    return getEmployeeDisplayName(employee);
  };

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'DimCompanyKey',
      'DimOrganisationKey',
      'DimDateKey',
      'DimEmployeeKey',
    ];
    
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => { 
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();
    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };


  const activeEmployees = useMemo(() => {
    return employees?.filter((employee) => employee[2]) || [];
  }, [employees]);

  const getUnmatchedEmployeeRecords = (rows) => {
    const activeEmployeeNames = new Set(
      activeEmployees.map((employee) => normalizeEmployeeName(getEmployeeLookupName(employee))),
    );
    const unmatched = {};

    rows.forEach((row) => {
      const rawName = getImportEmployeeName(row);
      const normalizedName = normalizeEmployeeName(rawName);

      if (!normalizedName || activeEmployeeNames.has(normalizedName)) return;

      if (!unmatched[normalizedName]) {
        unmatched[normalizedName] = {
          rawName,
          normalizedName,
          total: row.total || 0,
          chargeableHours: row['chargeable hours'] || row.chargeablehours || 0,
          nonChargeableHours: row['nonchargeable hours'] || row['non chargeable hours'] || row.nonchargeablehours || 0,
        };
      }
    });

    return Object.values(unmatched);
  };

  const openEmployeeImportModal = (records, transformedData) => {
    const initialResolutions = {};
    records.forEach((record) => {
      initialResolutions[record.normalizedName] = ADD_NEW_EMPLOYEE;
    });

    setPendingImportRows(transformedData);
    setEmployeeImportRecords(records);
    setEmployeeImportResolutions(initialResolutions);
  };

  const closeEmployeeImportModal = () => {
    setPendingImportRows([]);
    setEmployeeImportRecords([]);
    setEmployeeImportResolutions({});
  };

  const submitEmployeeUtilisationImport = (transformedData, employeeMappings = {}) => {
    ManageReportDataApi.importDataFromExcel(
      name,
      transformedData,
      selectedDates,
      selectedCompany,
      selectedOrganisation,
      { employee_mappings: employeeMappings },
    ).then((res) => {
      if (res.status === 200) {
        getStateData(res.data);
        if (typeof getEmployees === 'function') getEmployees();
      } else if (res.status === 409 && Array.isArray(res.missing_employees)) {
        const missingRecords = res.missing_employees.map((record) => ({
          rawName: record.name,
          normalizedName: record.normalized_name,
          total: record.total || 0,
          chargeableHours: record.chargeable_hours || 0,
          nonChargeableHours: record.nonchargeable_hours || 0,
        }));
        openEmployeeImportModal(missingRecords, transformedData);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleExcelImport = (updated_data) => {
    var transformedData = updated_data.map(function (obj) {
      var newObj = {};
      for (var key in obj) {
        if (obj.hasOwnProperty(key)) {
          newObj[key.toLowerCase()] = obj[key];
        }
      }
      return newObj;
    });

    // check import data is valid
    const validData = transformedData.every(item => {
      return (item.hasOwnProperty('name') || item.hasOwnProperty('employee') || item.hasOwnProperty('employee name') || item.hasOwnProperty('employeename'))
        && (item.hasOwnProperty('chargeable hours') || item.hasOwnProperty('chargeablehours'))
        && (item.hasOwnProperty('nonchargeable hours') || item.hasOwnProperty('non chargeable hours') || item.hasOwnProperty('nonchargeablehours'))
        && item.hasOwnProperty('total');
    });
    if (!validData) {
      toast.warning('Please insert the valid data. Data must contain name, chargeable hours, nonchargeable hours, and total.');
      return;
    }

    const unmatchedRecords = getUnmatchedEmployeeRecords(transformedData);
    if (unmatchedRecords.length > 0) {
      openEmployeeImportModal(unmatchedRecords, transformedData);
      return;
    }

    submitEmployeeUtilisationImport(transformedData);
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };
    
    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    if (
      field.column.key === 'AverageHoursWorkedPerDay' ||
      field.column.key === 'ActualFteEquivalentMTD' ||
      field.column.key === 'HoursWorkedMTD' ||
      field.column.key === 'ChargeableHoursWorkedMTD' ||
      field.column.key === 'NonChargeableHoursWorkedMTD'
    ) {
      if (isNaN(updated_data.updated[field.column.key])) {
        toast.warning('Please insert the number value.');
        return;
      }
    }

    if (field.column.key === 'HoursWorkedMTD') {
      if (Number(updatedRows[field.indexes[0]]['ChargeableHoursWorkedMTD']) + Number(updatedRows[field.indexes[0]]['NonChargeableHoursWorkedMTD']) !== Number(updated_data.updated[field.column.key])) {
        if (Number(updatedRows[field.indexes[0]]['ChargeableHoursWorkedMTD']) + Number(updatedRows[field.indexes[0]]['NonChargeableHoursWorkedMTD']) > Number(updated_data.updated[field.column.key])) {
          if (Number(updated_data.updated[field.column.key]) > Number(updatedRows[field.indexes[0]]['NonChargeableHoursWorkedMTD'])) {
            updated_data.updated['ChargeableHoursWorkedMTD'] = Number(updated_data.updated[field.column.key]) - Number(updatedRows[field.indexes[0]]['NonChargeableHoursWorkedMTD']);
            updated_data.updated['NonChargeableHoursWorkedMTD'] = Number(updatedRows[field.indexes[0]]['NonChargeableHoursWorkedMTD']);
          } else {
            updated_data.updated['ChargeableHoursWorkedMTD'] = Number(updated_data.updated[field.column.key]);
            updated_data.updated['NonChargeableHoursWorkedMTD'] = 0;
          }
        } else {
          if (Number(updated_data.updated[field.column.key]) > Number(updatedRows[field.indexes[0]]['NonChargeableHoursWorkedMTD'])) {
            updated_data.updated['ChargeableHoursWorkedMTD'] = Number(updated_data.updated[field.column.key]) - Number(updatedRows[field.indexes[0]]['NonChargeableHoursWorkedMTD']);
            updated_data.updated['NonChargeableHoursWorkedMTD'] = Number(updatedRows[field.indexes[0]]['NonChargeableHoursWorkedMTD']);
          } else {
            updated_data.updated['ChargeableHoursWorkedMTD'] = 0;
            updated_data.updated['NonChargeableHoursWorkedMTD'] = Number(updated_data.updated[field.column.key]);
          }
        }
      }
    } else if (field.column.key === 'ChargeableHoursWorkedMTD') {
      updated_data.updated['NonChargeableHoursWorkedMTD'] = Number(updatedRows[field.indexes[0]]['NonChargeableHoursWorkedMTD']) || 0;
      updated_data.updated['HoursWorkedMTD'] = Number(updated_data.updated[field.column.key]) + Number(updated_data.updated['NonChargeableHoursWorkedMTD']);
    } else if (field.column.key === 'NonChargeableHoursWorkedMTD') {
      updated_data.updated['ChargeableHoursWorkedMTD'] = Number(updatedRows[field.indexes[0]]['ChargeableHoursWorkedMTD']) || 0;
      updated_data.updated['HoursWorkedMTD'] = Number(updated_data.updated[field.column.key]) + Number(updated_data.updated['ChargeableHoursWorkedMTD']);
    }

    let updatedTableRows = [...updatedRows];
    
    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];      
    }

    updatedTableRows.map((row) => {
      if (updated_data.rowIds.includes(row.id)) {
        if (field.column.key === 'HoursWorkedMTD' || field.column.key === 'ChargeableHoursWorkedMTD' || field.column.key === 'NonChargeableHoursWorkedMTD') {
          row['HoursWorkedMTD'] = updated_data.updated['HoursWorkedMTD'];
          row['ChargeableHoursWorkedMTD'] = updated_data.updated['ChargeableHoursWorkedMTD'];
          row['NonChargeableHoursWorkedMTD'] = updated_data.updated['NonChargeableHoursWorkedMTD'];
        } else {
          row[field.column.key] = updatedRows[field.indexes[0]][field.column.key];
        }
      }
      return row;
    });

    // Always sort using the consistent sorting logic
    updatedTableRows.sort((a, b) => {
      // Handle null/undefined DimDateKey values
      if (!a.DimDateKey && !b.DimDateKey) return 0;
      if (!a.DimDateKey) return 1;  // Push null/undefined dates to end
      if (!b.DimDateKey) return -1; // Push null/undefined dates to end

      // First compare by date
      if (b.DimDateKey !== a.DimDateKey) {
        return b.DimDateKey - a.DimDateKey;
      }
      
      // If dates are equal, sort by employee
      // If both employee keys are null/undefined, keep original order
      if (!a.DimEmployeeKey && !b.DimEmployeeKey) return 0;
      if (!a.DimEmployeeKey) return 1;  // Push null/undefined employees to end
      if (!b.DimEmployeeKey) return -1; // Push null/undefined employees to end

      // Find employees
      const employeeA = employees.find((employee) => employee[0] === a.DimEmployeeKey);
      const employeeB = employees.find((employee) => employee[0] === b.DimEmployeeKey);

      // Handle cases where employee is not found in the employees array
      if (!employeeA && !employeeB) return 0;
      if (!employeeA) return 1;  // Push rows with missing employee data to end
      if (!employeeB) return -1; // Push rows with missing employee data to end

      // Compare employee names, handling potential null/undefined values
      const nameA = getEmployeeDisplayName(employeeA);
      const nameB = getEmployeeDisplayName(employeeB);
      return nameA.localeCompare(nameB);
    });

    setTableRows((currentRows) =>
      mergeEditedGridRows(
        currentRows,
        updatedTableRows,
        updated_data.rowIds,
        updated_data.updated,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) {
          toast.error(res.message);
        } else {
          resetReportingState();
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handleDateChange = (date, field) => {
    if (field === 'StartDate') {
      if (endDate) {
        if (
          new Date(date.$d).getTime() - 3600000 >
          new Date(endDate).getTime()
        ) {
          toast.warning(
            'Start date must be less than the end date!',
          );
          return;
        } else {
          setStartDate(date.$d);
        }
      } else setStartDate(date.$d);
    } else {
      if (startDate) {
        if (new Date(startDate).getTime() > new Date(date.$d).getTime()) {
          toast.warning(
            'End date must be greater than the start date!',
          );
          return;
        } else {
          setEndDate(date.$d);
        }
      } else setEndDate(date.$d);
    }
  };

  const handleExcelExport = (exportData) => {
    if (exportAllData) {
      if (exportData.length === 0)
        toast.warning('There is no export data!');
      setOpen(false);
    } else {
      if (startDate && endDate) {
        if (exportData.length === 0)
          toast.warning('There is no export data!');
        setOpen(false);
      } else {
        toast.warning(
          'Please select the start date and end date!',
        );
        return;
      }
    }
  };


  const handleEmployeeResolutionChange = (normalizedName, value) => {
    setEmployeeImportResolutions((current) => ({
      ...current,
      [normalizedName]: value,
    }));
  };

  const handleConfirmEmployeeImport = () => {
    const mappings = {};
    employeeImportRecords.forEach((record) => {
      mappings[record.normalizedName] = employeeImportResolutions[record.normalizedName] || ADD_NEW_EMPLOYEE;
    });

    const rowsToImport = pendingImportRows;
    closeEmployeeImportModal();
    submitEmployeeUtilisationImport(rowsToImport, mappings);
  };

  const employeeUtilisationColumns = useMemo(() => {
    let employee_utilisation_columns = [
      SelectColumn,
      {
        key: 'DimDateKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Period</b>
            {frozenColumns.indexOf('DimDateKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimDateKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimDateKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        sortable: true,
        frozen: frozenColumns.indexOf('DimDateKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimDateKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimDateKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {dates &&
                  dates.map((cur) => (
                    <option key={cur[0]} value={cur[0]}>
                      {cur[2] + ' ' + cur[3]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimDateKey ? props.row.DimDateKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimDateKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {dates &&
                dates.map((cur) => (
                  <MenuItem key={cur[0]} value={cur[0]}>
                    {cur[2] + ' ' + cur[3]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Employee</b>
            {frozenColumns.indexOf('DimEmployeeKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimEmployeeKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimEmployeeKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimEmployeeKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {getEmployeeDisplayName(cur)}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimEmployeeKey ? props.row.DimEmployeeKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {getEmployeeDisplayName(cur)}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'AverageHoursWorkedPerDay',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Avg Hours Per Day</b>
            {frozenColumns.indexOf('AverageHoursWorkedPerDay') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'AverageHoursWorkedPerDay')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'AverageHoursWorkedPerDay')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'AverageHoursWorkedPerDay',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'AverageHoursWorkedPerDay',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.AverageHoursWorkedPerDay}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    AverageHoursWorkedPerDay: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('AverageHoursWorkedPerDay') !== -1
            ? true
            : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.AverageHoursWorkedPerDay}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                AverageHoursWorkedPerDay: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'ActualFteEquivalentMTD',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>FTE</b>
            {frozenColumns.indexOf('ActualFteEquivalentMTD') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ActualFteEquivalentMTD')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ActualFteEquivalentMTD')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'ActualFteEquivalentMTD',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ActualFteEquivalentMTD',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 100,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ActualFteEquivalentMTD}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ActualFteEquivalentMTD: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('ActualFteEquivalentMTD') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ActualFteEquivalentMTD}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                ActualFteEquivalentMTD: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'HoursWorkedMTD',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Hours Worked</b>
            {frozenColumns.indexOf('HoursWorkedMTD') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'HoursWorkedMTD')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'HoursWorkedMTD')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'HoursWorkedMTD')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'HoursWorkedMTD',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.HoursWorkedMTD}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    HoursWorkedMTD: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('HoursWorkedMTD') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.HoursWorkedMTD}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                HoursWorkedMTD: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'ChargeableHoursWorkedMTD',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Chargeable Hours</b>
            {frozenColumns.indexOf('ChargeableHoursWorkedMTD') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ChargeableHoursWorkedMTD')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ChargeableHoursWorkedMTD')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'ChargeableHoursWorkedMTD',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ChargeableHoursWorkedMTD',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ChargeableHoursWorkedMTD}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ChargeableHoursWorkedMTD: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('ChargeableHoursWorkedMTD') !== -1
            ? true
            : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ChargeableHoursWorkedMTD}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                ChargeableHoursWorkedMTD: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'NonChargeableHoursWorkedMTD',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Non Chargeable Hours</b>
            {frozenColumns.indexOf('NonChargeableHoursWorkedMTD') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'NonChargeableHoursWorkedMTD')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'NonChargeableHoursWorkedMTD')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'NonChargeableHoursWorkedMTD',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'NonChargeableHoursWorkedMTD',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.NonChargeableHoursWorkedMTD}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    NonChargeableHoursWorkedMTD: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('NonChargeableHoursWorkedMTD') !== -1
            ? true
            : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.NonChargeableHoursWorkedMTD}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                NonChargeableHoursWorkedMTD: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
    ];

    if (orgLength.length > 1) {
      employee_utilisation_columns.splice(1, 0, {
        key: 'DimOrganisationKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Department</b>
            {frozenColumns.indexOf('DimOrganisationKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimOrganisationKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOrganisationKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimOrganisationKey != null &&
                props.row.DimOrganisationKey
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimOrganisationKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {organisations &&
                organisations.map(
                  (cur) =>
                    props.row.DimCompanyKey === cur.DimCompanyKey &&
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.OrgName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    if (companies.length > 1) {
      employee_utilisation_columns.splice(1, 0, {
        key: 'DimCompanyKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Company</b>
            {frozenColumns.indexOf('DimCompanyKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimCompanyKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimCompanyKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimCompanyKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimCompanyKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimCompanyKey != null && props.row.DimCompanyKey}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimCompanyKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {companies &&
                companies.map(
                  (cur) =>
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.CompanyName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    let frozenCols = employee_utilisation_columns.filter(
      (col) => col.frozen === true,
    );
    let unfrozenCols = employee_utilisation_columns.filter(
      (col) => col.frozen === false,
    );
    employee_utilisation_columns = [...frozenCols, ...unfrozenCols];

    return employee_utilisation_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    companies,
    employees,
    organisations,
    orgLength,
    dates,
    handleSelectFrozenColumns,
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.DimDateKey !== 'All'
          ? // eslint-disable-next-line
            r.DimDateKey == filters.DimDateKey
          : true) &&
        (filters.DimEmployeeKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeKey == filters.DimEmployeeKey
          : true) &&
        (filters.AverageHoursWorkedPerDay
          ? r.AverageHoursWorkedPerDay?.toString().includes(
              filters.AverageHoursWorkedPerDay.toString(),
            )
          : true) &&
        (filters.ActualFteEquivalentMTD
          ? r.ActualFteEquivalentMTD?.toString().includes(
              filters.ActualFteEquivalentMTD.toString(),
            )
          : true) &&
        (filters.HoursWorkedMTD
          ? r.HoursWorkedMTD?.toString().includes(
              filters.HoursWorkedMTD.toString(),
            )
          : true) &&
        (filters.ChargeableHoursWorkedMTD
          ? r.ChargeableHoursWorkedMTD?.toString().includes(
              filters.ChargeableHoursWorkedMTD.toString(),
            )
          : true) &&
        (filters.NonChargeableHoursWorkedMTD
          ? r.NonChargeableHoursWorkedMTD?.toString().includes(
              filters.NonChargeableHoursWorkedMTD.toString(),
            )
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const exportData = useMemo(() => {
    let export_data = [];

    if (exportAllData) {
      export_data = [...tableRows];
    } else {
      if (startDate && endDate) {
        let start_date =
          new Date(startDate).getFullYear() * 10000 +
          (new Date(startDate).getMonth() + 1) * 100 +
          1;
        let end_date =
          new Date(endDate).getFullYear() * 10000 +
          (new Date(endDate).getMonth() + 1) * 100 +
          1;
        export_data = [...tableRows];
        export_data = export_data.filter(
          (data) =>
            data.DimDateKey >= start_date && data.DimDateKey <= end_date,
        );
      }
    }

    return export_data;
  }, [exportAllData, tableRows, startDate, endDate]);
  
  const exportValue = useMemo(() => {
    let export_value = {
      Sheet1: {
        columns: [],
        data: [],
      },
    };

    const getPeriodLabel = (dimDateKey) => {
      if (!dimDateKey) return '';

      const dateRecord = dates?.find((date) => date[0] === dimDateKey);
      if (dateRecord) {
        return `${dateRecord[2]} ${dateRecord[3]}`;
      }

      const dateKeyText = dimDateKey.toString();
      if (dateKeyText.length >= 6) {
        const year = dateKeyText.slice(0, 4);
        const monthIndex = Number(dateKeyText.slice(4, 6)) - 1;
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];

        if (monthIndex >= 0 && monthIndex < monthNames.length) {
          return `${monthNames[monthIndex]} ${year}`;
        }
      }

      return dimDateKey;
    };

    export_value['Sheet1'].columns = [
      { title: 'Period', width: { wpx: 90 } },
      { title: 'Name', width: { wpx: 140 } },
      { title: 'Avg Hours Per Day', width: { wpx: 110 } },
      { title: 'FTE', width: { wpx: 80 } },
      { title: 'Total', width: { wpx: 80 } },
      { title: 'Chargeable Hours', width: { wpx: 120 } },
      { title: 'NonChargeable Hours', width: { wpx: 140 } },
    ];
    exportData.forEach((data) => {
      export_value['Sheet1'].data.push([
        {
          value: getPeriodLabel(data.DimDateKey),
        },
        {
          value: data.DimEmployeeKey
            ? employees.find((employee) => employee[0] === data.DimEmployeeKey)
              ? employees.find(
                  (employee) => employee[0] === data.DimEmployeeKey,
                )[1]
              : ''
            : '',
        },
        {
          value: data.AverageHoursWorkedPerDay ? Number(data.AverageHoursWorkedPerDay) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.ActualFteEquivalentMTD ? Number(data.ActualFteEquivalentMTD) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.HoursWorkedMTD ? Number(data.HoursWorkedMTD) : 0,
          style: { numFmt: '0' },
        },
        {
          value: data.ChargeableHoursWorkedMTD
            ? Number(data.ChargeableHoursWorkedMTD)
            : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.NonChargeableHoursWorkedMTD
            ? Number(data.NonChargeableHoursWorkedMTD)
            : 0,
          style: { numFmt: '0.00' },
        },
      ]);
    });
    return export_value;
  }, [dates, employees, exportData]);

  const importFlag = useMemo(() => {
    let synergyPages = selectedApis.find(
      (selectedApi) => selectedApi.ApiType === 'Synergy',
    );

    if (synergyPages) {
      if (synergyPages.ApiAccounts.includes('Employee Utilisation'))
        return false;
    }

    return true;
  }, [selectedApis]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        {importFlag ? <ImportExcel onImport={handleExcelImport} /> : null}
        <IconButton onClick={() => setOpen(true)}>
          <GetAppIcon />
        </IconButton>
        <Button
          onClick={() => onClickedAdd(name, 'auto')}
          variant="outlined"
          color="success"
        >
          Auto Add
        </Button>
        <IconButton
          onClick={() =>
            selectedDates.length === 1 ? onClickedAdd(name) : null
          }
        >
          <AddIcon />
        </IconButton>
        <IconButton onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={employeeUtilisationColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>

      <Dialog
        open={open}
        maxWidth="sm"
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Select Date Range'}</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={exportAllData}
                onChange={(event) => setExportAllData(event.target.checked)}
                name="exportAllData"
                color="success"
              />
            }
            label="Export all data"
          />
          {!exportAllData ? (
            <Grid container spacing={1}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="Start Date"
                      variant="inline"
                      value={dayjs(startDate)}
                      onChange={(event) => handleDateChange(event, 'StartDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="End Date"
                      variant="inline"
                      value={dayjs(endDate)}
                      onChange={(event) => handleDateChange(event, 'EndDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            color="error"
            size="small"
          >
            Cancel
          </Button>
          {exportData.length > 0 ? (
            <div onClick={() => setOpen(false)}>
              <ExportExcel filename="Employee Utilisation" value={exportValue} />
            </div>
          ) : (
            <Button
              onClick={() => handleExcelExport(exportData)}
              variant="outlined"
              color="success"
              size="small"
            >
              Export
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={employeeImportRecords.length > 0}
        maxWidth="md"
        fullWidth
        onClose={closeEmployeeImportModal}
      >
        <DialogTitle>{'Confirm Employee Matches'}</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 12 }}>
            The following imported names did not match an active employee. Choose an existing active employee or leave Add New selected.
          </div>
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #ddd' }}>Imported Name</th>
                  <th style={{ textAlign: 'right', padding: 8, borderBottom: '1px solid #ddd' }}>Total</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #ddd' }}>Use Employee</th>
                </tr>
              </thead>
              <tbody>
                {employeeImportRecords.map((record) => (
                  <tr key={record.normalizedName}>
                    <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{record.rawName}</td>
                    <td style={{ padding: 8, borderBottom: '1px solid #eee', textAlign: 'right' }}>{record.total}</td>
                    <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>
                      <Select
                        fullWidth
                        size="small"
                        value={employeeImportResolutions[record.normalizedName] || ADD_NEW_EMPLOYEE}
                        onChange={(event) => handleEmployeeResolutionChange(record.normalizedName, event.target.value)}
                      >
                        <MenuItem value={ADD_NEW_EMPLOYEE}>Add New Employee</MenuItem>
                        {activeEmployees.map((employee) => (
                          <MenuItem key={employee[0]} value={employee[0]}>
                            {getEmployeeDisplayName(employee)}
                          </MenuItem>
                        ))}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeEmployeeImportModal}
            variant="outlined"
            color="error"
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmEmployeeImport}
            variant="outlined"
            color="success"
            size="small"
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
}

EmployeeUtilisationTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,
  dates: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  employees: PropTypes.array.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  selectedOrganisation: PropTypes.number.isRequired,
  selectedDates: PropTypes.array.isRequired,
  selectedApis: PropTypes.array.isRequired,
  orgLength: PropTypes.array.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
  getEmployees: PropTypes.func,
  resetReportingState: PropTypes.func.isRequired,
};

export default EmployeeUtilisationTable;
